<script setup lang="ts" generic="T">
import { NuxtLink } from '#components'
import type { RouteLocationRaw } from '#vue-router'

const t2 = useT2({})

interface ItemUi {
  key: string | number
  img?: gen.ImageFragment | None
  title: string | None
  description?: string | None
  hasContent?: boolean
  back?: RouteLocationRaw | None
  to?: RouteLocationRaw | None
}

const props = defineProps<{
  items: T[] | None
  ui(i: T, index: number): ItemUi | None
}>()

const init3 = useInited('init3')
const activeIndex = defineModel<string | number>()

const arr = computed(() => {
  const { items, ui } = props
  return helpers.arr(items, (i, index) => {
    const item = ui(i, index)
    return item ? { org: i, item, index } : undefined
  })
})

const slots = useSlots()
const select123 = computed((): boolean => !!slots.default)

const arr2 = computed(() => {
  const id = activeIndex.value
  const select = select123.value
  return arr.value.map((item) => ({ ...item, active: id === item.item.key, select: item.item.hasContent && select }))
})

const data = shallowReactive({
  el: undefined as undefined | HTMLElement,
})

function toggle(e: unknown, { item, active, select }: Pick<(typeof arr2.value)[number], 'item' | 'select' | 'active'>) {
  if (item.to) return
  activeIndex.value = !item.hasContent || !select || active ? undefined : item.key
  data.el = helpers.getHTMLElement(helpers.isObject(e) && 'target' in e ? e.target : e)
}
function exit() {
  scrollToElem(data.el, { behavior: 'smooth', offsetTop: 200, tolarance: 200 })
  window.setTimeout(() => {
    activeIndex.value = -1
    data.el = undefined
  }, 100)
}
// https://www.lapetitejumelle.com/awards/
// https://www.jonite.com/specs
</script>

<template>
  <div :class="$style.col">
    <div v-for="{ item, org, index, active, select } in arr2" :key="item.key" :class="[$style.outer, select && $style.btn, active && 'on']">
      <component
        :is="item.to ? NuxtLink : select ? 'button' : 'div'"
        :class="[$style.box, select && 'ButtonIcon', active && 'on']"
        :to="activeIndex === item.key && item.back ? item.back : item.to"
        :type="!item.to && select ? 'button' : undefined"
        @click.passive="(e: unknown) => toggle(e, { active, item, select })"
      >
        <BaseImage v-if="item.img" :class="$style.img" :img="item.img" sizes="120px" ratio="square" fit="streach" />
        <span :class="[$style.txt, active && 'on']">
          <slot name="txt" v-bind="{ item: org, index }">
            <h3 v-if="item.title" :class="'m_fs1 _lh12'">{{ item.title }}</h3>
            <span v-if="item.description" class="_short _fs09 _pt04">{{ item.description }}</span>
          </slot>
        </span>

        <ButtonIcon v-if="select" :txt="t2('More')" to="span" :class="$style.icon2">
          <span :class="[$style.icon, active && 'on']"><LazyIconChevron v-if="init3" :open="!!active" size="_svg16" /></span>
        </ButtonIcon>
      </component>

      <UiGrow v-if="select" :open="!!active" destroy-on-close>
        <div class="_relative _bg0 _col _pt1 d_pb4 d_pl2 d_pr2 m_pl1 m_pr1 m_pb6">
          <slot v-bind="{ item: org, index, exit }" />
        </div>
      </UiGrow>
    </div>
  </div>
</template>

<style module>
.col {
  composes: global(_col);
  composes: global(_pb02);
  --pad: 20px;
}
:global(.m) .col {
  --pad: 10px;
}
.outer {
  composes: global(_relative);
  composes: global(_hidden);
  composes: global(_mb04);
  composes: global(m_mr04);
  composes: global(m_ml04);
  /* composes: global(_br9); */
  /* composes: global(_b5); */
  composes: global(_br22);
  composes: global(_b5);
  composes: global(_bg5);
  /* background: var(--black-5); */
  /* border-color: var(--black-3); */
}

.box {
  composes: global(_relative);

  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_gap1);

  composes: global(_pt2);
  composes: global(_pb2);
  composes: global(m_pl08);
  composes: global(d_pl3);
  composes: global(_pr04);

  composes: global(_hm80);
  composes: global(_w100p);

  composes: global(_c1);
  composes: global(_start);
}

.txt {
  composes: global(_col);
  composes: global(_ctrY);

  composes: global(_pr1);

  composes: global(_spaced);

  gap: 0px 14px;
  flex-grow: 9999;
  flex-shrink: 1;
  transition: padding 100ms var(--tran2);
}
.icon2 {
  composes: global(_c1);
  transition: transform 100ms var(--tran2);
}

.icon {
  composes: global(_ctr);
  composes: global(_h28);
  composes: global(_w28);
  composes: global(_br99);
}

.icon:global(.on) {
  color: var(--black);
  background: var(--bg);
}
:global(.m) .icon {
  color: var(--black);
}
.btn:hover,
.btn:global(.on) {
  z-index: 1;
  border-color: var(--black-3);
}

.btn .box::after {
  background-color: var(--bg);
  content: '';
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  transform-origin: bottom;
  width: 100%;
  z-index: -1;
  clip-path: inset(calc(100% - 7px) 7px 7px 7px round 9px);
  transition: clip-path 0.45s cubic-bezier(0.23, 1, 0.32, 1);
  opacity: 1;
}
:global(.d) .box:hover::after {
  clip-path: inset(0 0 0 0 round 9px);
}
.img {
  composes: global(_br9);
  composes: global(_h80);
  composes: global(_w80);
  composes: global(_noflex);
  transition: transform 100ms var(--tran2);
}

.box:global(.on) .icon2 {
  transform: translateX(-6px);
}
.box:global(.on) .img {
  transform: translateX(6px);
}
:global(.d) .outer:not(:global(.on)):hover .icon {
  color: var(--black);
}
.btn .box:global(.on)::after {
  clip-path: inset(0 -10px 0 -10px round 9px);
}
/* .btn:not(:global(.on)):hover > .txt,
.txt:global(.on) {
  padding: 0 0 0 var(--pad);
} */
</style>
